import { ThumbsUp } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const MAX_TOOLTIP_NAMES = 10;

interface LikeButtonProps {
  likedByMe?: boolean;
  likeCount?: number;
  likers?: string[];
  onClick: () => void;
  className?: string;
}

/**
 * Thumbs-up like toggle shared by team chat, task comments, client chat, and
 * the portal. When likers are known, hovering shows who liked; otherwise
 * (zero likes, or stale cached data without a likers array) it falls back to
 * the plain Like/Unlike title tooltip.
 */
export function LikeButton({ likedByMe, likeCount, likers, onClick, className }: LikeButtonProps) {
  const count = likeCount ?? 0;
  const button = (showTitle: boolean) => (
    <button
      type="button"
      title={showTitle ? (likedByMe ? "Unlike" : "Like") : undefined}
      onClick={onClick}
      className={`flex items-center gap-0.5 px-1 py-0.5 rounded transition-colors ${
        likedByMe ? "text-blue-600" : "text-muted-foreground hover:text-blue-500"
      } ${className ?? ""}`}
    >
      <ThumbsUp className="w-3 h-3" />
      {count > 0 && <span className="text-xs">{count}</span>}
    </button>
  );

  if (count > 0 && likers && likers.length > 0) {
    const shown = likers.slice(0, MAX_TOOLTIP_NAMES);
    const extra = likers.length - shown.length;
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>{button(false)}</TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            {shown.map((name, i) => (
              <div key={`${name}-${i}`}>{name}</div>
            ))}
            {extra > 0 && <div className="text-muted-foreground">+{extra} more</div>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button(true);
}
